/**
 * Meta por rota — SPA de index.html único.
 *
 * Atualiza `<title>`, `<meta name="description">`, `robots`, `canonical`, as
 * tags Open Graph/Twitter e o JSON-LD a cada navegação, lendo tudo de
 * [src/seo/site.js] (fonte única, compartilhada com o build).
 */

import { metaFor } from '@/seo/site';

function upsertMeta(selector, attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeEl(selector) {
  const el = document.head.querySelector(selector);
  if (el) el.parentNode.removeChild(el);
}

/**
 * Um `<script>` só, marcado com `data-seo="route"` — o mesmo marcador que o
 * build usa, para o bloco da rota substituir o do HTML estático em vez de
 * empilhar um segundo JSON-LD na página.
 */
function upsertJsonLd(nodes) {
  const selector = 'script[type="application/ld+json"][data-seo="route"]';
  let el = document.head.querySelector(selector);

  if (!nodes || !nodes.length) {
    if (el) el.parentNode.removeChild(el);
    return;
  }

  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo', 'route');
    document.head.appendChild(el);
  }

  el.textContent = JSON.stringify(nodes);
}

/**
 * Aplica o meta da rota.
 * @param {string} path - `to.path` do vue-router (sem query string).
 */
export function applyRouteMeta(path) {
  const m = metaFor(path);

  document.title = m.title;

  upsertMeta('meta[name="description"]', 'name', 'description', m.description);
  upsertMeta('meta[name="robots"]', 'name', 'robots', m.robots);

  // Canonical sem query string: `?ad=`, `?utm_*` e `?pxl=` dos anúncios
  // apontam todos para a mesma URL indexável.
  //
  // Numa 404 não há URL certa para apontar: em vez de herdar o canonical da
  // página anterior (o head é o mesmo documento na SPA), removemos os dois.
  if (m.canonical) {
    upsertLink('canonical', m.canonical);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', m.canonical);
  } else {
    removeEl('link[rel="canonical"]');
    removeEl('meta[property="og:url"]');
  }

  // Open Graph
  upsertMeta('meta[property="og:title"]', 'property', 'og:title', m.title);
  upsertMeta('meta[property="og:description"]', 'property', 'og:description', m.description);
  upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
  upsertMeta('meta[property="og:image"]', 'property', 'og:image', m.image);
  upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'ZaperMenu');
  upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', 'pt_BR');

  // Twitter
  upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
  upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', m.title);
  upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', m.description);
  upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', m.image);

  upsertJsonLd(m.jsonLd);
}
